/**
 * Drag handle that widens the left rail.
 *
 * Why a resizable rail rather than an expand-to-modal overlay: the assistant
 * proposes changes to the edit, and a modal covering the timeline and player
 * hides the very thing you are judging the proposal against. Widening in place
 * gives the conversation room — clip cards several across instead of two — while
 * the timeline stays visible.
 *
 * A full-screen "focus mode" overlay is still worth having for long reading
 * sessions, and would be cheap to add: the rail and any overlay are both just
 * views of `useChat` / `useAgent` / the CRDT, so there is no state to hand over.
 * Deferred deliberately, not forgotten.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const RAIL_MIN_WIDTH = 320;
export const RAIL_DEFAULT_WIDTH = 320;
/** Leave at least this much for the player + inspector, whatever the drag says. */
const CENTRE_MIN_WIDTH = 460;
const STORAGE_KEY = 'fabled.railWidth';
/** Arrow-key step, so the handle is usable without a pointer. */
const KEY_STEP = 24;

function maxWidth(): number {
  if (typeof window === 'undefined') return 760;
  return Math.max(RAIL_MIN_WIDTH, window.innerWidth - CENTRE_MIN_WIDTH);
}

export function clampRailWidth(width: number): number {
  return Math.round(Math.min(Math.max(width, RAIL_MIN_WIDTH), maxWidth()));
}

/** Same storage discipline as presence: unusable storage must not be fatal. */
function readStoredWidth(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null; // Safari private mode throws
  }
}

function writeStoredWidth(width: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    /* not fatal — the width just won't survive a reload */
  }
}

/**
 * The rail's width, persisted per browser. Re-clamped on window resize so a
 * width stored on a wide monitor cannot squeeze the editor on a laptop.
 */
export function useRailWidth(): [number, (width: number) => void] {
  const [width, setWidth] = useState(() => clampRailWidth(readStoredWidth() ?? RAIL_DEFAULT_WIDTH));

  useEffect(() => {
    const onResize = () => setWidth((w) => clampRailWidth(w));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const set = useCallback((next: number) => {
    const clamped = clampRailWidth(next);
    setWidth(clamped);
    writeStoredWidth(clamped);
  }, []);

  return [width, set];
}

export default function RailResizer({
  width,
  onResize,
}: {
  width: number;
  onResize: (width: number) => void;
}) {
  const dragging = useRef(false);

  useEffect(() => {
    function onMove(e: PointerEvent) {
      if (!dragging.current) return;
      // The rail starts at the viewport's left edge, so the pointer's x IS the
      // width. No offset bookkeeping, and it stays correct if the drag starts
      // slightly off-centre on the handle.
      onResize(e.clientX);
    }
    function onUp() {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.classList.remove('is-col-resizing');
    }
    // Listeners live on the window, not the handle: a fast drag outruns a 6px
    // target and would otherwise stick mid-resize.
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [onResize]);

  return (
    <div
      className="rail-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      aria-valuenow={width}
      aria-valuemin={RAIL_MIN_WIDTH}
      aria-valuemax={maxWidth()}
      tabIndex={0}
      onPointerDown={(e) => {
        e.preventDefault(); // don't start a text selection
        dragging.current = true;
        document.body.classList.add('is-col-resizing');
      }}
      onDoubleClick={() => onResize(RAIL_DEFAULT_WIDTH)}
      onKeyDown={(e) => {
        // Keyboard resizing: a pointer-only splitter is unusable for anyone
        // who cannot drag.
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          onResize(width - KEY_STEP);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          onResize(width + KEY_STEP);
        } else if (e.key === 'Home') {
          e.preventDefault();
          onResize(RAIL_DEFAULT_WIDTH);
        }
      }}
      title="Drag to resize — double-click to reset"
    />
  );
}
